import { FullscreenOutlined } from '@ant-design/icons';
import { Card, message, Pagination, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import MyDateSelector from '../../component/MyDateSelector';
import TableHeaderSection from '../../component/TableHeaderSection';
import baseuri from '../../methods/baseuri';
import { setColor, truncateAddress } from '../../methods/utils/general-utils';
import { MyEmptyTable } from '../../robots/email-robot/EmailRobot';
import { MyTableCard } from '../../users/users-list/UsersByCountry';
import { MyTable } from '../../users/users-list/UsersList';

export const UsersListPage = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;
export const MainSection = styled(Card)`
	border-radius: 10px;
	gap: 2em;
`;
const DeletedDoubleFundings = () => {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState({
		itemsList: [],
		paginator: {} as any,
	});

	const columns = [
		{
			key: 'userEmail',
			title: 'Email',
			render: (text: any) => text.userEmail,
		},
		{
			key: 'status',
			title: 'Status',
			dataIndex: 'status',
			render: (x: any) => <Tag color={setColor(x)}>{x}</Tag>,
		},
		{
			key: 'fundingStatus',
			title: 'Funding Status',
			dataIndex: 'fundingStatus',
			render: (x: any) => <Tag color={setColor(x)}>{x}</Tag>,
		},
		{
			key: '5423545',
			title: 'Amount Received',
			dataIndex: 'amountReceived',
			render: (x: any) => `${x} BUSD`,
		},
		{
			key: '435362',
			title: 'Amount Received',
			dataIndex: 'totalAmountDeposited',
			render: (x: any) => `${x} BUSD`,
		},
		{
			key: '3452',
			title: 'Deposit Address',
			dataIndex: 'depositAddress',
			render: (x: any) => (
				<>
					<Tooltip title="Click to copy address" color="green">
						<span
							role="none"
							onClick={() => {
								navigator.clipboard.writeText(x);
								message.success('Address copied');
							}}
						>
							{truncateAddress(x)}
						</span>
					</Tooltip>
					<Tooltip title="View on BscScan" color="blue">
						<Link
							to={`https://bscscan.com/address/${x}`}
							target="_blank"
							rel="noreferrer"
						>
							<FullscreenOutlined />
						</Link>
					</Tooltip>
				</>
			),
		},
		{
			key: 'receivingAddress',
			title: 'Receiving Address',
			dataIndex: 'receivingAddress',
			render: (x: any) => (
				<>
					{' '}
					<Tooltip title="Click to copy address" color="green">
						<span
							role="none"
							onClick={() => {
								navigator.clipboard.writeText(x);
								message.success('Address copied');
							}}
						>
							{truncateAddress(x)}
						</span>
					</Tooltip>
					<Tooltip title="View on BscScan" color="blue">
						<Link
							to={`https://bscscan.com/address/${x}`}
							target="_blank"
							rel="noreferrer"
						>
							<FullscreenOutlined />
						</Link>
					</Tooltip>
				</>
			),
		},
		{
			key: 99,
			title: 'Processed By',
			dataIndex: 'processList',
			render: (text: any) => <p>{text?.creator?.email}</p>,
		},
		{
			key: 4,
			title: 'Date Updated',
			dataIndex: 'updatedAt',
			render: (x: any) => (
				<>
					{' '}
					{moment(x).format('MMM Do YY, h:mm:ss a')}{' '}
					<strong>{moment(x).fromNow()}</strong>
				</>
			),
		},
		{
			key: 49,
			title: 'Date Created',
			dataIndex: 'createdAt',
			render: (x: any) => moment(x).format('MMM Do YY, h:mm:ss a'),
		},
	];

	const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();

	const getQueryParams = () => {
		const params1 = Object.fromEntries(searchParams.entries());
		return {
			...params1,
			startDate: `${
				searchParams.get('startDate') ||
				moment('2021/10/01').format('YYYY-MM-DD')
			}`,
			endDate: `${
				searchParams.get('endDate') || moment().format('YYYY-MM-DD')
			}`,
			perPage: `${searchParams.get('perPage') || '10'}`,
			pageId: `${searchParams.get('pageId') || '1'}`,
			sort: `${searchParams.get('sort') || 'desc'}`,
		};
	};

	const handleSearchByDate = async () => {
		const params1 = getQueryParams();
		setLoading(true);
		setData({
			itemsList: [],
			paginator: {} as any,
		});
		try {
			const res: any = await baseuri.get(
				'admin/compliance/deleted-double-fundings',
				{
					params: {
						...params1,
					},
				}
			);

			if (res.status === 200) {
				setData({ ...data, ...res.data.data });
				message.success('Successfully fetched records');
			}
			setLoading(false);
		} catch (error) {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (searchParams.get('userId')) {
			handleSearchByDate();
		}
	}, [location.search]);
	return (
		<UsersListPage>
			<MainSection>
				<MyDateSelector />
				<MyTableCard>
					<TableHeaderSection
						tableName="Deleted Double Fundings"
						currentPage={data?.paginator?.currentPage || 0}
						totalRecords={data?.paginator?.itemCount || 0}
					/>
					<MyTable
						locale={{
							emptyText: <MyEmptyTable />,
						}}
						loading={loading}
						columns={columns}
						dataSource={data.itemsList}
						pagination={false}
					/>

					<Pagination
						onChange={(page, pageSize) => {
							const params1 = Object.fromEntries(searchParams.entries());
							setSearchParams({
								...params1,
								pageId: `${page}`,
								perPage: `${pageSize}`,
							});
						}}
						disabled={loading}
						pageSize={data?.paginator?.perPage || 0}
						current={data?.paginator?.currentPage || 0}
						total={data?.paginator?.itemCount || 0}
						showSizeChanger
						size="small"
						showQuickJumper
					/>
				</MyTableCard>
			</MainSection>
		</UsersListPage>
	);
};

export default DeletedDoubleFundings;
